/**
 * ParentHeartsStore.js — Hearts oversight: each linked teen's live balance,
 * how Hearts work, recent activity, and pending big-ticket approvals.
 */
import React, { useEffect, useState } from 'react';
import { getLocalPrice } from '../utils/displayCurrency';
import { Heart, Loader2, Check, X, Sparkles, Trophy, Flame, ShoppingBag, Package, CreditCard, Gem, Snowflake, Users, Gift } from 'lucide-react';
import { ParentShell } from '../components/parent/ParentShell';
import { apiClient } from '../api/client';

const EARN_WAYS = [
  { icon: Trophy, label: 'Completing challenges', body: 'Every real-world challenge beaten pays out Hearts.' },
  { icon: Flame, label: 'Keeping streaks', body: 'Daily practice streaks multiply what they earn.' },
  { icon: ShoppingBag, label: 'Spending', body: 'Practice credits, streak freezes and collectibles — big spends come to you first.' },
];

const USES = [
  { icon: Gem, title: 'Claim Limited-Edition NFT Drops', body: 'Weekly rare and legendary collectibles for their Vault (50–200 Hearts each)' },
  { icon: Snowflake, title: 'Freeze a Slipping Streak', body: 'Spend 50 Hearts to protect their daily-practice streak on a tough day' },
  { icon: Users, title: 'Cheer On a Friend', body: "Stake Hearts on a friend's weekly goal — they win, you both win" },
  { icon: Gift, title: 'Gift Hearts to Anyone on HiBFF', body: 'Send Hearts to a friend or family member as a boost' },
];

const PACK_STYLES = [
  { icon: Heart, iconBg: 'bg-purple-100', iconText: 'text-purple-600', price: 'text-purple-600', save: null },
  { icon: Package, iconBg: 'bg-purple-100', iconText: 'text-purple-600', price: 'text-purple-600', save: 'Save 20%' },
  { icon: CreditCard, iconBg: 'bg-orange-100', iconText: 'text-orange-500', price: 'text-orange-600', save: 'Save 30%' },
];

const PACK_KEY = { 50: 'pack_small', 200: 'pack_medium', 500: 'pack_large' };
const formatPrice = (pricePence, currency, hearts) => {
  const key = PACK_KEY[hearts];
  if (key) return getLocalPrice(key).text;
  return `\u00A3${(pricePence / 100).toFixed(2)}`;
};

const ParentHeartsStore = () => {
  const [teens, setTeens] = useState(null);
  const [pending, setPending] = useState([]);
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [buyingPack, setBuyingPack] = useState(null);
  const [buyTeenId, setBuyTeenId] = useState(null);

  const load = async () => {
    const [ins, p, pk] = await Promise.all([
      apiClient.request('/api/parent/insights').catch(() => ({ teens: [] })),
      apiClient.request('/api/hearts/approvals/pending').catch(() => ({ pending: [] })),
      apiClient.request('/api/billing/hearts-packs').catch(() => ({ packs: [] })),
    ]);
    setTeens(ins?.teens || []);
    setPending(p?.pending || []);
    setPacks(pk?.packs || []);
    setBuyTeenId(prev => prev || ins?.teens?.[0]?.teen_id || null);
  };

  useEffect(() => { load(); }, []);

  const act = async (id, decision) => {
    setBusy(true);
    try {
      await apiClient.request(`/api/hearts/approvals/${id}/${decision}`, { method: 'POST' });
      load();
    } catch (e) { alert(e?.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const buyPack = async (packId) => {
    if (!buyTeenId) return;
    setBuyingPack(packId);
    try {
      const res = await apiClient.request('/api/billing/checkout/hearts', {
        method: 'POST',
        body: JSON.stringify({ pack_id: packId, origin_url: window.location.origin, target_teen_id: buyTeenId }),
      });
      if (res?.url) { window.location.href = res.url; return; }
      alert('Could not start checkout. Please try again.');
    } catch (e) { alert(e?.message || 'Could not start checkout'); }
    setBuyingPack(null);
  };

  const buyTeenName = teens?.find(t => t.teen_id === buyTeenId)?.teen_first_name || 'your teen';

  return (
    <ParentShell activeId="hearts" title="Hearts">
      <div className="px-6 lg:px-10 py-8 lg:py-10 space-y-8 max-w-5xl">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-white shadow-lg">
            <Heart size={22} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Hearts Oversight</h2>
            <p className="text-sm text-gray-500">Live balances, what Hearts mean, and spends waiting on your call.</p>
          </div>
        </div>

        {teens === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rose-500" size={28} /></div>
        ) : (
          <>
            {/* Per-teen balances */}
            {teens.length === 0 ? (
              <p className="text-sm text-gray-400 py-8 text-center border-2 border-dashed border-gray-200 rounded-2xl">
                No linked teens yet — sign the Charter from your Dashboard to link your teen.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hearts-teen-cards">
                {teens.map(t => (
                  <div key={t.teen_id} className="rounded-3xl bg-gradient-to-br from-rose-500 to-pink-600 text-white p-7 shadow-xl" data-testid={`hearts-card-${t.teen_id}`}>
                    <p className="text-xs uppercase tracking-widest font-bold opacity-80">{t.teen_first_name}&apos;s Hearts</p>
                    <p className="text-5xl font-black mt-2">{(t.hearts_balance ?? 0).toLocaleString()}</p>
                    <p className="mt-3 text-sm opacity-90">
                      {t.badges_earned} badge{t.badges_earned === 1 ? '' : 's'} · {t.challenges_completed} challenge{t.challenges_completed === 1 ? '' : 's'} completed
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Hearts packs — buyable by the parent, same packs teens see */}
            <section className="rounded-3xl bg-white border border-gray-100 p-7 shadow-sm" data-testid="parent-hearts-packs">
              <h3 className="font-bold text-gray-900 text-lg mb-1">The Hearts Store — buy for your teen</h3>
              <p className="text-sm text-gray-500 mb-3">
                These are the exact packs your teen sees when they tap <strong>&quot;Ask my parent&quot;</strong> —
                but you can also buy directly, any time. Hearts land straight in their balance.
              </p>
              {teens.length > 1 && (
                <div className="mb-5 flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">Buying for:</span>
                  <select value={buyTeenId || ''} onChange={e => setBuyTeenId(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm bg-white"
                    data-testid="hearts-buy-teen-select">
                    {teens.map(t => (
                      <option key={t.teen_id} value={t.teen_id}>{t.teen_first_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {packs.map((pack, i) => {
                  const s = PACK_STYLES[i] || PACK_STYLES[0];
                  const Icon = s.icon;
                  const best = i === 1;
                  return (
                    <div key={pack.id}
                      className={`p-6 bg-white rounded-xl text-center relative ${best ? 'border-2 border-purple-500 shadow-lg' : 'border border-gray-200 shadow-sm'}`}
                      data-testid={`parent-pack-${pack.id}`}>
                      {best && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                          BEST VALUE
                        </div>
                      )}
                      <img src={['/packs/spark.png', '/packs/growth.png', '/packs/champion.png'][i]} alt={pack.name} className="w-20 h-20 mx-auto object-contain mb-3" data-testid={`parent-pack-art-${pack.id || i}`} />
                      <p className="text-xl font-bold text-gray-900">{pack.name}</p>
                      <p className={`text-3xl font-black mt-1 ${s.price}`}>{formatPrice(pack.price, pack.currency, pack.hearts)}</p>
                      {s.save && <p className="text-sm text-green-600 font-semibold mt-1.5">{s.save}</p>}
                      <button onClick={() => buyPack(pack.id)}
                        disabled={!buyTeenId || buyingPack !== null}
                        className={`mt-4 w-full py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:shadow-lg disabled:opacity-50 ${best ? 'bg-purple-600 hover:bg-purple-500' : 'bg-gray-900 hover:bg-gray-800'}`}
                        data-testid={`buy-pack-${pack.id}`}>
                        {buyingPack === pack.id ? 'Opening checkout…' : `Buy for ${buyTeenName}`}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-4">Payment goes through Stripe from your card — your teen never sees prices or pays anything.</p>
            </section>

            {/* What can your teen do with Hearts? */}
            <section className="rounded-3xl bg-gray-50 border border-gray-100 p-7" data-testid="parent-hearts-uses">
              <h3 className="font-bold text-gray-900 text-lg mb-5">What can your teen do with Hearts?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                {USES.map(u => {
                  const Icon = u.icon;
                  return (
                    <div key={u.title} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-9 h-9 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mt-0.5">
                        <Icon size={17} />
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900">{u.title}</p>
                        <p className="text-sm text-gray-600 mt-0.5">{u.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-5">
                Hearts add flexible extras alongside your plan. They don&apos;t change how many practice partners your teen
                can save — that&apos;s set by your plan tier (Entry 3, Challenger 8, The Elite 20, Master 38).
              </p>
            </section>

            {/* How Hearts work */}
            <section className="rounded-3xl bg-white border border-gray-100 p-7 shadow-sm">
              <h3 className="font-bold text-gray-900 text-lg mb-4">How Hearts work</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {EARN_WAYS.map(w => {
                  const Icon = w.icon;
                  return (
                    <div key={w.label} className="rounded-2xl bg-rose-50/60 p-4">
                      <Icon size={20} className="text-rose-500 mb-2" />
                      <p className="font-semibold text-gray-900 text-sm">{w.label}</p>
                      <p className="text-xs text-gray-600 mt-1">{w.body}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-4">Per the Charter you signed: Hearts belong to your teen and can never be confiscated.</p>
            </section>

            {/* Recent activity per teen */}
            {teens.some(t => (t.recent_activity || []).length > 0) && (
              <section className="rounded-3xl bg-white border border-gray-100 p-7 shadow-sm" data-testid="hearts-recent-activity">
                <h3 className="font-bold text-gray-900 text-lg mb-4">Recent earning activity</h3>
                {teens.map(t => (t.recent_activity || []).length > 0 && (
                  <div key={t.teen_id} className="mb-4 last:mb-0">
                    <p className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mb-2">{t.teen_first_name}</p>
                    <ul className="divide-y divide-gray-50">
                      {t.recent_activity.slice(0, 5).map((a, i) => (
                        <li key={i} className="py-2.5 flex items-center gap-3 text-sm">
                          <span className="text-base">{a.icon || '⭐'}</span>
                          <span className="flex-1 text-gray-700">{a.text}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">{a.at ? new Date(a.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : ''}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
            )}

            {/* Approvals */}
            <section data-testid="pending-approvals">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Pending big-ticket approvals</h3>
              {pending.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center border-2 border-dashed border-gray-200 rounded-2xl">
                  Nothing waiting on your call right now.
                </p>
              ) : (
                <ul className="space-y-3">
                  {pending.map((r) => (
                    <li key={r.id} className="rounded-2xl bg-white border border-gray-100 p-5 flex items-center gap-4 shadow-sm">
                      <Sparkles className="text-amber-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{r.item_name || r.title || 'Reward request'}</p>
                        <p className="text-xs text-gray-500">{r.hearts || r.amount} hearts · requested {r.requested_at_label || 'recently'}</p>
                      </div>
                      <button onClick={() => act(r.id, 'approve')} disabled={busy}
                        className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                        <Check size={14} /> Approve
                      </button>
                      <button onClick={() => act(r.id, 'decline')} disabled={busy}
                        className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                        <X size={14} /> Decline
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </ParentShell>
  );
};

export default ParentHeartsStore;
